'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { subscribeUser } from '../app/actions';
import { SignatureContext } from '../context/SignatureContext';

function urlBase64ToUint8Array(base64String: string) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function PushNotificationPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const { signature } = useContext(SignatureContext);

  useEffect(() => {
    if (!('Notification' in window)) { return; }

    // Check if user has previously declined
    const hasDeclined = localStorage.getItem('notificationsDeclined');

    if (signature && Notification.permission === 'default' && !hasDeclined) {
      setShowPrompt(true);
    }
  }, [signature]);

  const handleEnableNotifications = async () => {
    try {
      if (!signature) {
        toast.error('Please confirm your wallet first');
        return;
      }

      if (!('serviceWorker' in navigator)) {
        toast.error('Service workers are not supported in this browser');
        return;
      }

      // Register service worker if not already registered
      let registration;
      try {
        registration = await navigator.serviceWorker.ready;
      } catch (_error) {
        // If service worker isn't ready, try registering it
        registration = await navigator.serviceWorker.register('/sw.js');
        await navigator.serviceWorker.ready;
      }

      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        try {
          const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(
              process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY!,
            ),
          });

          const serializedSubscription = {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: btoa(
                String.fromCharCode(
                  ...new Uint8Array(subscription.getKey('p256dh')!),
                ),
              ),
              auth: btoa(
                String.fromCharCode(
                  ...new Uint8Array(subscription.getKey('auth')!),
                ),
              ),
            },
          };

          await subscribeUser(serializedSubscription);
          toast.success('Notifications enabled successfully!');
          setShowPrompt(false);
        } catch (subError) {
          console.error('Subscription error:', subError);
          toast.error('Failed to subscribe to push notifications');
        }
      } else if (permission === 'denied') {
        localStorage.setItem('notificationsDeclined', 'true');
        setShowPrompt(false);
        toast.error('Notification permission denied');
      }
    } catch (error) {
      console.error('Error enabling notifications:', error);
      toast.error('Failed to enable notifications');
    }
  };

  const handleDecline = () => {
    // Store the user's choice in localStorage
    localStorage.setItem('notificationsDeclined', 'true');
    setShowPrompt(false);
  };

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className='fixed right-4 bottom-4 left-4 z-50 md:right-4 md:left-auto md:w-96'
        >
          <div className='rounded-xl border border-gray-700/50 bg-gray-800/95 p-4 shadow-lg backdrop-blur-sm'>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className='mb-2 font-semibold text-lg text-white'>
                  Enable Notifications
                </h3>
                <p className='mb-4 text-gray-300 text-sm'>
                  Stay updated with new posts, comments, and important updates!
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={handleEnableNotifications}
                    className='flex-1 rounded-lg bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600'
                  >
                    Enable
                  </button>
                  <button
                    onClick={handleDecline}
                    className='rounded-lg bg-gray-700 px-4 py-2 text-gray-300 transition-colors hover:bg-gray-600'
                  >
                    Later
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
